/**
 * Meeting Members Page
 * /meetings/:id/members - 모임 멤버 목록 페이지
 */

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { getDisplayName, getAvatarUrl } from "@/utils/displayName";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useLocation, useRoute } from "wouter";

interface MeetingMember {
  id: string;
  nickname: string;
  nickname_display?: string; // OZK 표준 필드
  displayName: string;
  avatarUrl: string | null;
  role: "leader" | "assistant" | "member";
  location?: string;
  suburb?: string;
  mbti?: string;
  badges: string[];
  bio?: string;
}

export default function MeetingMembersPage() {
  const [match, params] = useRoute<{ id: string }>("/meetings/:id/members");
  const [, navigate] = useLocation();
  const { user } = useAuth();

  const meetingId = params?.id;

  // 모임 기본 정보 조회
  const { data: meetingData } = useQuery<{
    ok: boolean;
    data: { id: string; title: string };
  }>({
    queryKey: ["/api/meetings", meetingId],
    queryFn: async () => {
      if (!meetingId) throw new Error("Invalid meeting ID");
      const response = await fetch(`/api/meetings/${meetingId}`);
      if (!response.ok) throw new Error("Failed to fetch meeting");
      return response.json();
    },
    enabled: !!meetingId,
  });

  // 멤버 목록 조회
  const { data: membersData, isLoading } = useQuery<{
    ok: boolean;
    data: {
      meetingId: string;
      total: number;
      members: MeetingMember[];
    };
  }>({
    queryKey: ["/api/meetings", meetingId, "members"],
    queryFn: async () => {
      if (!meetingId) throw new Error("Invalid meeting ID");
      const response = await fetch(`/api/meetings/${meetingId}/members`);
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Failed to fetch members:", response.status, errorText);
        throw new Error(`Failed to fetch members: ${response.status}`);
      }
      const json = await response.json();

      // API 응답 구조: { ok: true, data: { ok: true, data: { meetingId, total, members } } }
      let membersData: any = null;

      if (json.ok && json.data) {
        // 중첩된 경우: { ok: true, data: { ok: true, data: {...} } }
        if (json.data.ok && json.data.data) {
          membersData = json.data.data;
        }
        // 단일 중첩: { ok: true, data: { meetingId, total, members } }
        else if (json.data.members || json.data.total !== undefined) {
          membersData = json.data;
        }
        // 그 외
        else {
          membersData = json.data;
        }
      }
      // 직접 구조: { meetingId, total, members }
      else if (json.members || json.total !== undefined) {
        membersData = json;
      }

      // 최종 검증 및 기본값 설정
      if (!membersData || !Array.isArray(membersData.members)) {
        console.warn("Invalid members data structure:", membersData);
        membersData = { meetingId, total: 0, members: [] };
      }

      return { ok: true, data: membersData };
    },
    enabled: !!meetingId,
  });

  const meeting = meetingData?.ok ? meetingData.data : null;
  const members =
    membersData?.ok &&
    membersData.data?.members &&
    Array.isArray(membersData.data.members)
      ? membersData.data.members
      : [];
  const membersTotal =
    membersData?.ok && membersData.data?.total ? membersData.data.total : 0;

  if (!match || !params) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">
              유효하지 않은 모임 ID입니다
            </p>
            <Button variant="outline" onClick={() => navigate("/meetings")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">로딩 중...</p>
          </Card>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="p-12 text-center">
            <p className="text-muted-foreground mb-4">
              모임 정보를 찾을 수 없습니다
            </p>
            <Button variant="outline" onClick={() => navigate("/meetings")}>
              목록으로
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* 헤더 */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/meetings/${meetingId}`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold">
            {meeting.title} - 멤버 {membersTotal}명
          </h1>
        </div>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card>
          <CardHeader>
            <CardTitle>멤버 목록</CardTitle>
          </CardHeader>
          <CardContent>
            {Array.isArray(members) && members.length > 0 ? (
              <div className="space-y-4">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-start gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <Avatar className="w-12 h-12 flex-shrink-0">
                      <AvatarImage src={getAvatarUrl(member) || undefined} />
                      <AvatarFallback>
                        {getDisplayName(member)?.[0] || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">
                          {getDisplayName(member)}
                        </span>
                        {member.role === "leader" && (
                          <>
                            <Badge variant="secondary" className="text-xs">
                              방장
                            </Badge>
                            <span>👑</span>
                          </>
                        )}
                        {member.role === "assistant" && (
                          <Badge variant="secondary" className="text-xs">
                            부방장
                          </Badge>
                        )}
                        {member.badges.map((badge) => (
                          <Badge
                            key={badge}
                            variant="outline"
                            className="text-xs"
                          >
                            {badge === "premium" && "프리미엄"}
                          </Badge>
                        ))}
                      </div>
                      {member.location && (
                        <p className="text-sm text-muted-foreground">
                          📍 {member.location}
                        </p>
                      )}
                      {member.suburb && (
                        <p className="text-xs text-muted-foreground">
                          {member.suburb}
                        </p>
                      )}
                      {member.mbti && (
                        <p className="text-xs text-muted-foreground">
                          MBTI: {member.mbti}
                        </p>
                      )}
                      {member.bio && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-3">
                          {member.bio}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p className="text-sm">아직 멤버가 없습니다</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
